import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useStore } from '@feathr/extender/state';

import useGoogleAdsPermissions from './useGoogleAdsPermissions';
import { showGoogleAdsQuotaToast } from './useGoogleAdsQuotaToast.utils';

/**
 * On mount, shows the limit-reached toast if Google's shared daily API limit is reached.
 * Does nothing for an account that isn't connected to Google Ads.
 */
function useGoogleAdsQuotaToast(): void {
  const { GoogleAdsCustomers } = useStore();
  const { t } = useTranslation();
  const hasGoogleAds = useGoogleAdsPermissions();

  useEffect(() => {
    if (!hasGoogleAds) {
      return undefined;
    }

    let isCancelled = false;

    async function showToastIfExhausted(): Promise<void> {
      try {
        const { exhausted } = await GoogleAdsCustomers.getQuotaStatus();
        if (isCancelled || !exhausted) {
          return;
        }
        showGoogleAdsQuotaToast(t, 'page_load');
      } catch {
        // The status only enables an extra heads-up, so a failed check stays quiet.
      }
    }

    void showToastIfExhausted();

    return (): void => {
      isCancelled = true;
    };
  }, [GoogleAdsCustomers, hasGoogleAds, t]);
}

export default useGoogleAdsQuotaToast;
